import React, { ReactNode, RefObject, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Switch } from 'antd';

import { GenAiSpark } from 'assets/svg/landing/landingIcons';
import { ArrowRightIcon } from 'assets/svg/landing/searchIcons';
import StudioCardIcon from 'components/atoms/Landing/StudioCardIcon';
import LandingSearchCard from 'components/molecules/Landing/LandingSearchCard';
import LandingSearchSuggestion from 'components/molecules/Landing/LandingSearchSuggestion';
import { MAX_SUGGESTIONS, SEARCH_PLACEHOLDER_CARDS } from 'constants/landingSearch';
import {
    RGM_HOME_SEARCH_AI_MODE,
    RGM_HOME_SEARCH_EMPTY,
    RGM_HOME_SEARCH_INPUT,
    RGM_HOME_SEARCH_PANEL,
    RGM_HOME_SEARCH_PLACEHOLDERS,
    RGM_HOME_SEARCH_RESULTS,
    RGM_HOME_SEARCH_SEND,
    RGM_HOME_SEARCH_SHORTCUT,
    RGM_HOME_SEARCH_SUGGESTIONS,
    rgmSearchPlaceholderTestId,
    rgmSearchResultTestId,
    rgmSearchSuggestionTestId,
} from 'constants/landingTestIds';
import useSearchableRoutes from 'hooks/landing/useSearchableRoutes';
import { matchRoutes, RouteSearchEntry } from 'utils/landingSearch';
import { trackEventMixpanel } from 'utils/mixpanel';
import { LANDING_SEARCH_NAVIGATE, LANDING_SEARCH_SUBMIT } from 'utils/mixpanelEventNames';

interface Props {
    query: string;
    /** True once Enter or the send button has been pressed; swaps suggestions for results. */
    isSubmitted: boolean;
    placeholder: string;
    inputRef: RefObject<HTMLInputElement>;
    /** Hides the AI Mode switch for tenants without AskAsper — there is nowhere to send them. */
    isAskAsperEnabled: boolean;
    isAiMode: boolean;
    onQueryChange: (value: string) => void;
    onSubmit: () => void;
    onAiModeChange: (next: boolean) => void;
}

const DIVIDER = <div className="h-px w-full shrink-0 bg-filterV2Border" />;

const ASK_ASPER_ROUTE = '/ask-asper';

interface AiModeToggleProps {
    isAiMode: boolean;
    onAiModeChange: (next: boolean) => void;
}

/** With AI Mode on, submitting hands the query to AskAsper instead of matching it against routes. */
const AiModeToggle = ({ isAiMode, onAiModeChange }: AiModeToggleProps) => (
    <div className="flex shrink-0 items-center gap-3">
        <span data-testid={RGM_HOME_SEARCH_AI_MODE}>
            <Switch className="landing-ai-mode-switch" checked={isAiMode} onChange={onAiModeChange} />
        </span>
        <p className="whitespace-nowrap font-xxs font-normal text-promoColour">AI Mode</p>
    </div>
);

/** "Results" heading plus the AI Mode switch. */
const ResultsHeader = ({ aiMode }: { aiMode: ReactNode }) => (
    <div className="flex w-full items-center justify-between">
        <p className="font-md font-semibold text-filterV2TextPrimary">Results</p>
        {aiMode}
    </div>
);

/** Idle state — four prompts routing to AskAsper, shown until the user types. */
const PlaceholderGrid = ({ onSelect }: { onSelect?: (question: string) => void }) => (
    <>
        <div className="flex w-full items-center justify-between">
            <p className="font-md font-semibold text-filterV2TextPrimary">What do you want to do today?</p>
            <p className="shrink-0 whitespace-nowrap font-xs font-medium text-navV2TextMuted">
                Based on common RGM workflows
            </p>
        </div>
        <div data-testid={RGM_HOME_SEARCH_PLACEHOLDERS} className="grid w-full grid-cols-2 gap-4">
            {SEARCH_PLACEHOLDER_CARDS.map(({ id, title, description, Icon, bgClassName }) => (
                <LandingSearchCard
                    key={id}
                    testId={rgmSearchPlaceholderTestId(id)}
                    title={title}
                    description={description}
                    onClick={onSelect ? () => onSelect(description) : undefined}
                    icon={
                        <span
                            className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${bgClassName}`}
                        >
                            <Icon className="size-5" />
                        </span>
                    }
                />
            ))}
        </div>
    </>
);

interface SuggestionListProps {
    query: string;
    suggestions: RouteSearchEntry[];
    aiMode: ReactNode;
    onSelect: (label: string) => void;
}

/**
 * Typing state — matching route labels, then the raw query as the final row so the user can
 * always submit exactly what they typed.
 */
const SuggestionList = ({ query, suggestions, aiMode, onSelect }: SuggestionListProps) => (
    <>
        <ResultsHeader aiMode={aiMode} />
        <div data-testid={RGM_HOME_SEARCH_SUGGESTIONS} className="flex w-full flex-col gap-6">
            {suggestions.map((entry) => (
                <LandingSearchSuggestion
                    key={entry.id}
                    label={entry.label}
                    query={query}
                    onSelect={onSelect}
                    testId={rgmSearchSuggestionTestId(entry.id)}
                />
            ))}
            <LandingSearchSuggestion
                label={query}
                query={query}
                onSelect={onSelect}
                testId={rgmSearchSuggestionTestId('query')}
            />
        </div>
        {DIVIDER}
    </>
);

interface ResultListProps {
    query: string;
    results: RouteSearchEntry[];
    aiMode: ReactNode;
    onSelect: (entry: RouteSearchEntry) => void;
}

/** Submitted state — one card per matching route. */
const ResultList = ({ query, results, aiMode, onSelect }: ResultListProps) => (
    <>
        <ResultsHeader aiMode={aiMode} />
        {results.length ? (
            <div data-testid={RGM_HOME_SEARCH_RESULTS} className="flex w-full flex-col gap-4">
                {results.map((entry) => (
                    <LandingSearchCard
                        key={entry.id}
                        testId={rgmSearchResultTestId(entry.id)}
                        title={entry.label}
                        onClick={() => onSelect(entry)}
                        icon={<StudioCardIcon icon={entry.icon} groupKey={entry.id} />}
                    />
                ))}
            </div>
        ) : (
            <p data-testid={RGM_HOME_SEARCH_EMPTY} className="w-full break-words font-sm text-navV2TextMuted">
                {`No results for "${query}"`}
            </p>
        )}
        {DIVIDER}
    </>
);

/**
 * The expanded hero search.
 *
 * Positioned absolutely over the collapsed field by `LandingHero`, which keeps the collapsed
 * field's box in flow so opening the panel never shifts the page beneath it.
 *
 * Everything here is client-side: the index comes from the tenant config already in memory. When
 * a search service arrives, `useSearchableRoutes` is the only thing that has to change.
 */
function LandingSearchPanel({
    query,
    isSubmitted,
    placeholder,
    inputRef,
    isAskAsperEnabled,
    isAiMode,
    onQueryChange,
    onSubmit,
    onAiModeChange,
}: Props) {
    const navigate = useNavigate();
    const index = useSearchableRoutes();
    const results = useMemo(() => matchRoutes(index, query), [index, query]);

    /** AskAsper picks the question up from route state and asks it on arrival. */
    const askAsper = (question: string) => {
        navigate(ASK_ASPER_ROUTE, { state: { prefillQuestion: question } });
    };

    const submit = () => {
        const question = query.trim();
        if (!question) return;
        trackEventMixpanel(LANDING_SEARCH_SUBMIT, { query, resultCount: results.length, aiMode: isAiMode });
        if (isAiMode) {
            askAsper(question);
            return;
        }
        onSubmit();
    };

    // Mirrors the LANDING_STUDIO_NAVIGATE payload so both landing navigation surfaces report alike.
    const goToRoute = (entry: RouteSearchEntry) => {
        trackEventMixpanel(LANDING_SEARCH_NAVIGATE, {
            screen: entry.label,
            screenHeader: entry.groupLabel,
            url: entry.url,
        });
        navigate(entry.url);
    };

    const selectSuggestion = (label: string) => {
        if (isAiMode) {
            askAsper(label);
            return;
        }
        onQueryChange(label);
        onSubmit();
    };

    const aiMode = isAskAsperEnabled ? <AiModeToggle isAiMode={isAiMode} onAiModeChange={onAiModeChange} /> : null;

    const renderBody = () => {
        // The idle state has no "Results" row to sit on, so the switch gets its own, keeping the
        // same right-aligned position it holds once results appear.
        if (!query) {
            return (
                <>
                    {aiMode && <div className="flex w-full justify-end">{aiMode}</div>}
                    <PlaceholderGrid onSelect={isAskAsperEnabled ? askAsper : undefined} />
                </>
            );
        }
        if (!isSubmitted) {
            return (
                <SuggestionList
                    query={query}
                    suggestions={results.slice(0, MAX_SUGGESTIONS)}
                    aiMode={aiMode}
                    onSelect={selectSuggestion}
                />
            );
        }
        return <ResultList query={query} results={results} aiMode={aiMode} onSelect={goToRoute} />;
    };

    return (
        <div
            data-testid={RGM_HOME_SEARCH_PANEL}
            className="absolute inset-x-0 top-0 z-20 flex w-full flex-col gap-6 rounded-[16px] border border-white bg-white p-6 shadow-landingSearchExpanded"
        >
            <form
                className="flex w-full items-center justify-between"
                onSubmit={(event) => {
                    event.preventDefault();
                    submit();
                }}
            >
                <div className="flex min-w-px flex-[1_0_0] items-center gap-4">
                    <span className="flex size-6 shrink-0 items-center justify-center">
                        <GenAiSpark className="size-5" />
                    </span>
                    <input
                        ref={inputRef}
                        data-testid={RGM_HOME_SEARCH_INPUT}
                        type="text"
                        aria-label={placeholder}
                        placeholder={placeholder}
                        value={query}
                        onChange={(event) => onQueryChange(event.target.value)}
                        className="w-full border-none bg-transparent font-lg text-filterV2TextPrimary outline-none placeholder:text-navV2TextMuted"
                    />
                </div>
                <div className="flex shrink-0 items-center gap-3.5">
                    {!query && (
                        <span
                            data-testid={RGM_HOME_SEARCH_SHORTCUT}
                            className="rounded-[6px] bg-landingSearchShortcutBg px-2 py-1 font-xxs font-semibold text-navV2TextMuted"
                        >
                            ⌘K
                        </span>
                    )}
                    <button
                        type="submit"
                        data-testid={RGM_HOME_SEARCH_SEND}
                        aria-label="Search"
                        className="flex size-9 shrink-0 items-center justify-center rounded-[18px] bg-filterV2TextPrimary"
                    >
                        <ArrowRightIcon className="size-4" />
                    </button>
                </div>
            </form>

            {DIVIDER}
            {renderBody()}
        </div>
    );
}

export default LandingSearchPanel;
